import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowLeft, Lock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';

const TEXTURE = '/garde-valley.jpg';

export default function ResetPasswordConfirm() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [done, setDone] = useState(false);
    const [hasRecoverySession, setHasRecoverySession] = useState(false);
    const [linkInvalid, setLinkInvalid] = useState(false);

    useEffect(() => {
        // Flux principal : lien email → ?token_hash=...&type=recovery
        // consommé via verifyOtp (indépendant de l'allowlist Supabase).
        const tokenHash = searchParams.get('token_hash');
        if (tokenHash) {
            supabase.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash }).then(({ data, error }) => {
                if (error || !data?.session) {
                    setLinkInvalid(true);
                } else {
                    setHasRecoverySession(true);
                }
            });
        }
        // Compat : ancien flux hash-fragment traité par detectSessionInUrl.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
            if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
                setHasRecoverySession(true);
            }
        });
        supabase.auth.getSession().then(({ data }) => {
            if (data?.session) setHasRecoverySession(true);
        });
        return () => subscription.unsubscribe();
        // eslint-disable-next-line
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (password.length < 8) {
            setError('Le mot de passe doit contenir au moins 8 caractères.');
            return;
        }
        if (password !== confirm) {
            setError('Les deux mots de passe ne correspondent pas.');
            return;
        }
        setLoading(true);
        const { error } = await supabase.auth.updateUser({ password });
        setLoading(false);
        if (error) {
            setError(error.message || 'Erreur — le lien est peut-être expiré. Recommencez la procédure.');
            return;
        }
        setDone(true);
        setTimeout(() => navigate('/auth/signin'), 3500);
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="grid lg:grid-cols-2 min-h-screen">
                <div className="hidden lg:block relative">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: `url("${TEXTURE}")`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                        }}
                    />
                    <div
                        className="absolute inset-x-0 top-0 h-24 pointer-events-none"
                        style={{ backgroundImage: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)' }}
                    />
                    <div className="relative h-full flex flex-col p-10">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-background/85 hover:text-background text-sm transition-colors w-fit"
                        >
                            ← Retour à l'accueil
                        </Link>
                    </div>
                </div>

                <div className="flex items-center justify-center p-6 lg:p-12">
                    <div className="w-full max-w-md">
                        <Link
                            to="/auth/signin"
                            className="inline-flex items-center gap-1.5 text-sm text-foreground/65 hover:text-primary mb-8"
                        >
                            <ArrowLeft size={14} /> Connexion
                        </Link>

                        {!done ? (
                            <>
                                <div className="h-12 w-12 rounded-full bg-primary-light text-primary flex items-center justify-center mb-5">
                                    <Lock size={22} />
                                </div>
                                <h1 className="font-heading text-3xl mb-3">Nouveau mot de passe</h1>
                                <p className="text-foreground/70 leading-[1.7] mb-8">
                                    Choisissez un mot de passe robuste — au moins <strong>8 caractères</strong>,
                                    avec idéalement chiffres et caractères spéciaux.
                                </p>

                                {linkInvalid && (
                                    <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2 mb-5" data-testid="reset-link-invalid">
                                        Ce lien est invalide ou a expiré. Recommencez la procédure depuis la{' '}
                                        <Link to="/auth/reset" className="underline">page mot de passe oublié</Link>.
                                    </div>
                                )}
                                {!hasRecoverySession && !linkInvalid && (
                                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-5">
                                        Vérification du lien en cours…
                                        Si rien ne se passe, le lien est peut-être expiré — recommencez la
                                        procédure depuis la <Link to="/auth/reset" className="underline">page mot de passe oublié</Link>.
                                    </div>
                                )}

                                <form onSubmit={submit} className="space-y-5" data-testid="reset-confirm-form">
                                    <div className="space-y-2">
                                        <Label htmlFor="password">Nouveau mot de passe</Label>
                                        <Input
                                            id="password" type="password" required minLength={8}
                                            value={password} onChange={(e) => setPassword(e.target.value)}
                                            className="h-12" data-testid="reset-confirm-password"
                                            disabled={!hasRecoverySession}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="confirm">Confirmation</Label>
                                        <Input
                                            id="confirm" type="password" required minLength={8}
                                            value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                            className="h-12" data-testid="reset-confirm-confirm"
                                            disabled={!hasRecoverySession}
                                        />
                                    </div>
                                    {error && (
                                        <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                                            {error}
                                        </div>
                                    )}
                                    <Button
                                        type="submit" disabled={loading || !hasRecoverySession}
                                        className="w-full h-12 bg-primary hover:bg-primary-hover text-primary-foreground"
                                        data-testid="reset-confirm-submit"
                                    >
                                        {loading ? <Loader2 className="animate-spin" size={16} /> : 'Mettre à jour le mot de passe'}
                                    </Button>
                                </form>
                            </>
                        ) : (
                            <div className="space-y-5" data-testid="reset-confirm-success">
                                <div className="h-12 w-12 rounded-full bg-secondary-light text-secondary flex items-center justify-center">
                                    <CheckCircle2 size={22} />
                                </div>
                                <h1 className="font-heading text-3xl">Mot de passe mis à jour.</h1>
                                <p className="text-foreground/75 leading-[1.7]">
                                    Vous allez être redirigé vers la page de connexion dans 3 secondes…
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
